import {ReleaseMarker, ScheduledJobPerformanceDay, ScheduledJobPerformanceMetrics} from '../../services/api/jobPerformanceApi';

// Pure helpers for the Job Performance admin page (deepcontext-l6wj). The
// daily-aggregate endpoint always returns a fixed lookback window ending
// today (no offset/anchor param), so Older/Newer/Latest page through a
// larger client-side buffer instead of re-querying the backend.

/** Latest possible window start (right-aligned) for a window of `size` over `total` days. */
export function latestWindowStart(size: number, total: number): number {
    return Math.max(0, total - size);
}

/** Clamp an arbitrary window start into [0, latestWindowStart(size, total)]. */
export function clampWindowStart(start: number, size: number, total: number): number {
    const max = latestWindowStart(size, total);
    return Math.max(0, Math.min(start, max));
}

/** Releases whose deploy date falls within the visible days (inclusive). */
export function releasesInWindow(releases: ReleaseMarker[], days: ScheduledJobPerformanceDay[]): ReleaseMarker[] {
    if (days.length === 0) return [];
    const first = days[0].date;
    const last = days[days.length - 1].date;
    return releases.filter((r) => {
        const d = r.deployedAt.slice(0, 10);
        return d >= first && d <= last;
    });
}

/** The most recently deployed release, or undefined if the list is empty. */
export function latestRelease(releases: ReleaseMarker[]): ReleaseMarker | undefined {
    return releases.reduce<ReleaseMarker | undefined>((latest, r) => {
        if (!latest || r.deployedAt > latest.deployedAt) return r;
        return latest;
    }, undefined);
}

/**
 * Run-weighted average of `metricKey` over the days before vs. on/after
 * `releaseDateStr` (a `YYYY-MM-DD` string), returning the delta (after -
 * before). Each day is weighted by its run count so zero-run days (no
 * scanning activity) don't drag the average toward 0. Undefined when
 * there's no release date, or when either side has zero total runs — no
 * data is not the same as a 0% metric.
 */
export function splitMetricByRelease(
    days: ScheduledJobPerformanceDay[],
    releaseDateStr: string | undefined,
    metricKey: keyof ScheduledJobPerformanceMetrics,
): {before: number; after: number; delta: number} | undefined {
    if (!releaseDateStr) return undefined;
    const before = days.filter((d) => d.date < releaseDateStr);
    const after = days.filter((d) => d.date >= releaseDateStr);
    if (before.length === 0 || after.length === 0) return undefined;

    const weightedAvg = (rows: ScheduledJobPerformanceDay[]) => {
        const totalRuns = rows.reduce((sum, d) => sum + d.runs, 0);
        if (totalRuns === 0) return undefined;
        return rows.reduce((sum, d) => sum + (d[metricKey] as number) * d.runs, 0) / totalRuns;
    };

    const beforeAvg = weightedAvg(before);
    const afterAvg = weightedAvg(after);
    if (beforeAvg === undefined || afterAvg === undefined) return undefined;
    return {before: beforeAvg, after: afterAvg, delta: afterAvg - beforeAvg};
}

/**
 * Same as `splitMetricByRelease`, but computed over the SAME set of jobs on
 * both sides of the release so campaign churn (jobs that only ran before or
 * only after the deploy) can't masquerade as a deploy effect. Each entry in
 * `jobDaySets` is one job's day slice; a job qualifies only if it has >0
 * total runs strictly before `releaseDateStr` AND >0 total runs on/after it
 * (exactly when `splitMetricByRelease` resolves for that job).
 *
 * Each qualifying job's own before/after is still run-weighted across ITS
 * days, but the jobs are then averaged with EQUAL weight (deepcontext-s46g):
 * the delta answers "how did a typical blueprint move", so a job running 10x
 * as often as another can't move it any further than that other job does.
 */
export function sameJobsSplitMetricByRelease(
    jobDaySets: ScheduledJobPerformanceDay[][],
    releaseDateStr: string | undefined,
    metricKey: keyof ScheduledJobPerformanceMetrics,
): {before: number; after: number; delta: number} | undefined {
    if (!releaseDateStr) return undefined;

    const perJob = jobDaySets
        .map((days) => splitMetricByRelease(days, releaseDateStr, metricKey))
        .filter((split): split is NonNullable<typeof split> => split !== undefined);
    if (perJob.length === 0) return undefined;

    const mean = (pick: (s: {before: number; after: number}) => number) =>
        perJob.reduce((sum, s) => sum + pick(s), 0) / perJob.length;
    const before = mean((s) => s.before);
    const after = mean((s) => s.after);
    return {before, after, delta: after - before};
}

/** SUM() semantics for a nullable count: null + null = null, otherwise ignore the nulls. Mirrors the backend's own `addNullable` (scheduledJobPerformanceService.ts). */
function addNullable(a: number | null, b: number | null): number | null {
    if (a == null && b == null) return null;
    return (a ?? 0) + (b ?? 0);
}

/**
 * Sum-based before/after split for `matchedBidRequests` (deepcontext-ge91.2)
 * — an additive count, not a percentage, so each side is SUMmed rather than
 * run-weight-averaged (unlike `splitMetricByRelease`). Undefined when
 * there's no release date, either side has no days, or either side's sum is
 * null (SQL SUM-of-NULLs semantics: no bid-request data at all on that
 * side).
 */
export function splitBidRequestsByRelease(
    days: ScheduledJobPerformanceDay[],
    releaseDateStr: string | undefined,
): {before: number; after: number; delta: number} | undefined {
    if (!releaseDateStr) return undefined;
    const before = days.filter((d) => d.date < releaseDateStr);
    const after = days.filter((d) => d.date >= releaseDateStr);
    if (before.length === 0 || after.length === 0) return undefined;

    const sum = (rows: ScheduledJobPerformanceDay[]) => rows.reduce<number | null>((acc, d) => addNullable(acc, d.matchedBidRequests), null);
    const beforeSum = sum(before);
    const afterSum = sum(after);
    if (beforeSum === null || afterSum === null) return undefined;
    return {before: beforeSum, after: afterSum, delta: afterSum - beforeSum};
}

/**
 * Aggregate a slice of days into a single window metric: additive counts
 * (runs/completedRuns/urlsProcessed) are summed, percentages are run-weighted
 * averages across the visible days so zero-run days (no scanning activity)
 * don't drag the average toward 0. The backend's own `totals` field
 * aggregates over whatever `days` param was requested (always the full
 * fetch buffer here), so KPI/table values must be recomputed from the
 * VISIBLE slice instead. `matchedBidRequests` is SUMmed like the other
 * counts, but with SQL SUM-of-NULLs semantics (deepcontext-ge91.2).
 */
export function aggregateWindow(days: ScheduledJobPerformanceDay[]): ScheduledJobPerformanceMetrics {
    const totalRuns = days.reduce((s, d) => s + d.runs, 0);
    const sum = (key: 'runs' | 'completedRuns' | 'urlsProcessed' | 'matchedUrls') => days.reduce((s, d) => s + d[key], 0);
    const weightedAvg = (key: 'fetchFulfillmentPct' | 'matchRatePct' | 'dropRatePct') =>
        totalRuns === 0 ? 0 : days.reduce((s, d) => s + d[key] * d.runs, 0) / totalRuns;
    return {
        runs: sum('runs'),
        completedRuns: sum('completedRuns'),
        urlsProcessed: sum('urlsProcessed'),
        fetchFulfillmentPct: weightedAvg('fetchFulfillmentPct'),
        matchRatePct: weightedAvg('matchRatePct'),
        dropRatePct: weightedAvg('dropRatePct'),
        // Pooled (summed) like matchedBidRequests, not per-blueprint averaged
        // like the percentages above (deepcontext-0dl7).
        matchedUrls: sum('matchedUrls'),
        matchedBidRequests: days.reduce<number | null>((acc, d) => addNullable(acc, d.matchedBidRequests), null),
    };
}

/**
 * The all-jobs window aggregate, averaged PER BLUEPRINT (deepcontext-s46g):
 * counts are summed across jobs, but each percentage is the unweighted mean of
 * the jobs' own window percentages (the very numbers the per-job table shows),
 * over jobs with >0 runs in the window. Pooling instead let one 100k-URL job
 * dictate the headline; this answers "how does a typical blueprint perform".
 *
 * `jobDaySets` is one visible-day slice per job, or null/undefined while any
 * job's breakdown is still loading — then the result is undefined so the KPI
 * strip renders a placeholder rather than a number that shifts on arrival.
 */
export function macroAggregateWindow(
    jobDaySets: ScheduledJobPerformanceDay[][] | null | undefined,
): ScheduledJobPerformanceMetrics | undefined {
    if (!jobDaySets) return undefined;

    const perJob = jobDaySets.map(aggregateWindow);
    const active = perJob.filter((m) => m.runs > 0);
    const sum = (key: 'runs' | 'completedRuns' | 'urlsProcessed') => perJob.reduce((s, m) => s + m[key], 0);
    const mean = (key: 'fetchFulfillmentPct' | 'matchRatePct' | 'dropRatePct') =>
        active.length === 0 ? 0 : active.reduce((s, m) => s + m[key], 0) / active.length;
    return {
        runs: sum('runs'),
        completedRuns: sum('completedRuns'),
        urlsProcessed: sum('urlsProcessed'),
        fetchFulfillmentPct: mean('fetchFulfillmentPct'),
        matchRatePct: mean('matchRatePct'),
        dropRatePct: mean('dropRatePct'),
        matchedUrls: perJob.reduce((s, m) => s + m.matchedUrls, 0),
        matchedBidRequests: perJob.reduce<number | null>((acc, m) => addNullable(acc, m.matchedBidRequests), null),
    };
}

/**
 * True when the visible window ends on today's date — the last day's numbers
 * are still accumulating and the chart should mark that segment as partial.
 */
export function isTodayPartial(days: ScheduledJobPerformanceDay[], atLatest: boolean, todayIso: string): boolean {
    if (!atLatest || days.length === 0) return false;
    return days[days.length - 1].date === todayIso;
}

/** The version active on `dateStr` — the latest release deployed on or before it. */
export function versionActiveOnDate(releases: ReleaseMarker[], dateStr: string): string | undefined {
    const eligible = releases.filter((r) => r.deployedAt.slice(0, 10) <= dateStr);
    return latestRelease(eligible)?.version;
}
